import AnimateInView from '@atoms/AnimateInView/AnimateInView';
import Cta from '@atoms/Cta/Cta';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import Text from '@atoms/Text/Text';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import SiteEnum from '@models/enums/SiteEnum';
import { fadeInUp } from '@utility/config/animations';
import useIsTouchDevice from '@utility/hooks/useIsTouchDevice';
import useMediaQuery from '@utility/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, Variants, motion } from 'motion/react';
import { useState, useTransition } from 'react';

type IServiceLink = {
  readonly heading: string;
  readonly subText: string;
  readonly image: IImage;
  readonly cta: ILink;
};

export interface IServiceAccordion {
  readonly site: SiteEnum;
  readonly heading: string;
  readonly links: IServiceLink[];
}

const ServiceAccordion = ({ heading, links, site }: IServiceAccordion) => {
  const initialAccordionState = new Array(links.length).fill(false);
  const [accordionState, setAccordionState] = useState<Array<boolean>>([
    true,
    ...initialAccordionState.slice(1),
  ]);
  const [activeImage, setActiveImage] = useState<IImage | null>(
    links[0]?.image || null
  );
  const [previousImage, setPreviousImage] = useState<IImage | null>(null);
  const isMobile = useMediaQuery(1408);
  const isTouch = useIsTouchDevice();

  const [_, startTransition] = useTransition();

  const handleClick = (index: number, image: IImage) => {
    let newState = isMobile ? accordionState : initialAccordionState;
    newState[index] = !newState[index];

    startTransition(() => {
      setAccordionState(newState);
      setPreviousImage(activeImage);
      setActiveImage(image);
    });
  };

  const containerVariants: Variants = {
    enter: {
      height: 'auto',
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1,
        duration: 0.5,
        ease: 'easeInOut',
      },
    },
    exit: {
      height: 0,
      transition: {
        duration: 0.5,
        ease: 'easeInOut',
      },
    },
  };

  const childVariants: Variants = {
    enter: {
      y: 0,
      opacity: 1,
      transition: {
        delay: 0.1,
        duration: 0.5,
        ease: 'easeInOut',
      },
    },
    exit: {
      y: 20,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
  };

  const imageVariants: Variants = {
    enter: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
  };

  const AccordionTriggerTag = isMobile || isTouch ? 'button' : 'a';

  return (
    <AnimateInView className="tw-grid-container">
      <motion.div
        variants={fadeInUp}
        className="tw-col-span-full tw-flex tw-flex-col tw-justify-between xl:tw-col-span-5"
      >
        <Text variant="headline" className="tw-mb-lg">
          {heading}
        </Text>
        <div className="tw-relative">
          <div className="tw-w-full tw-border-b tw-border-grey-20 xl:tw-absolute xl:tw-bottom-0">
            {links.map(({ heading, subText, cta, image }, index) => (
              <div
                key={index}
                className={classNames(
                  'tw-border-t tw-transition-colors',
                  accordionState[index]
                    ? 'tw-border-black'
                    : 'tw-border-grey-20'
                )}
              >
                <AccordionTriggerTag
                  id={`accordion-button-${index}`}
                  href={!isMobile && !isTouch ? cta.url : undefined}
                  aria-controls={`accordion-panel-${index}`}
                  aria-expanded={accordionState[index]}
                  className="tw-flex tw-w-full tw-items-center tw-justify-between tw-py-sm tw-text-left xl:tw-py-md"
                  onMouseEnter={
                    !isMobile && !isTouch
                      ? () => handleClick(index, image)
                      : undefined
                  }
                  onClick={
                    isMobile || isTouch
                      ? () => handleClick(index, image)
                      : undefined
                  }
                  role={isMobile || isTouch ? 'button' : 'link'}
                  target={!isMobile && !isTouch ? cta.target : undefined}
                >
                  <Text variant="sub-headline">{heading}</Text>
                  <motion.div
                    animate={{ rotate: accordionState[index] ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="xl:tw-hidden"
                  >
                    <Icon
                      name="chevron-down"
                      className={classNames(
                        accordionState[index]
                          ? 'tw-text-black'
                          : 'tw-text-grey-40',
                        'rtl:tw-rotate-180'
                      )}
                    />
                  </motion.div>
                  <div className="max-xl:tw-hidden">
                    <Icon
                      name="arrow-right"
                      size="1.5rem"
                      className={classNames(
                        accordionState[index]
                          ? 'tw-text-black'
                          : 'tw-text-grey-40',
                        'rtl:tw-rotate-180'
                      )}
                    />
                  </div>
                </AccordionTriggerTag>

                <AnimatePresence mode="wait">
                  {accordionState[index] && (
                    <motion.div
                      id={`accordion-panel-${index}`}
                      role="region"
                      aria-labelledby={`accordion-button-${index}`}
                      layout
                      className="tw-overflow-hidden"
                      variants={containerVariants}
                      initial="exit"
                      animate="enter"
                      exit="exit"
                      key="service-content"
                    >
                      <motion.div
                        variants={childVariants}
                        initial="exit"
                        animate="enter"
                        exit="exit"
                        className="tw-grid tw-columns-1 tw-grid-cols-1 tw-gap-md tw-pb-sm md:tw-grid-cols-2 xl:tw-grid-cols-1 xl:tw-pb-md"
                      >
                        <div className="tw-flex tw-flex-col tw-gap-6">
                          <Text
                            variant="body-small"
                            className="tw-line-clamp-3 tw-pr-md"
                          >
                            {subText}
                          </Text>
                          {isMobile || isTouch ? (
                            <Cta
                              site={site}
                              text={cta.text}
                              url={cta.url}
                              target={cta.target}
                            />
                          ) : null}
                        </div>
                        <Image
                          classes="xl:tw-hidden"
                          image={image}
                          imageClasses="tw-w-full tw-max-w-[343px] md:tw-max-w-none"
                          aspectRatio={1}
                        />
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
      <motion.div
        variants={fadeInUp}
        className="tw-col-span-full tw-hidden tw-items-center xl:tw-col-span-6 xl:tw-col-start-7 xl:tw-flex"
      >
        <div className="tw-relative tw-flex tw-aspect-square tw-w-full">
          <AnimatePresence initial={false}>
            {previousImage && (
              <motion.div
                key={previousImage.url}
                variants={imageVariants}
                initial="enter"
                animate="enter"
                exit="exit"
                className="tw-absolute tw-h-full tw-w-full"
              >
                <Image
                  image={previousImage}
                  aspectRatio={1}
                  imageClasses="tw-w-full tw-object-cover tw-h-full"
                />
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence initial={false}>
            {activeImage && (
              <motion.div
                key={activeImage.url}
                variants={imageVariants}
                initial="exit"
                animate="enter"
                exit="exit"
                className="tw-absolute tw-h-full tw-w-full"
              >
                <Image
                  image={activeImage}
                  aspectRatio={1}
                  imageClasses="tw-w-full tw-object-cover tw-h-full"
                  sizes="50vw"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimateInView>
  );
};

export default ServiceAccordion;
